import {isSavingAllowed} from '@gaia/hooks/useAreas';
import useIsSaved from '@gaia/hooks/useIsSaved';
import {useUser} from '@gaia/stores/user';
import type {ImportState, SharedData} from '@gaia/types';
import saveTrack, {getIsTrackModifiedSinceSave} from '@gaia/utils/gaia/saveTrack';
import {useCallback, useEffect, useState} from 'react';

import bulkArchive from './dispatchers/bulkArchive';
import create from './dispatchers/create';
import remove from './dispatchers/remove';
import takeLocal from './dispatchers/takeLocal';
import update from './dispatchers/update';
import updatePointProperties from './dispatchers/updatePointProperties';
import updateProperties from './dispatchers/updateProperties';

export type TrackDispatcher = (action: TrackAction) => string[];

export function useTracks(
    project: MapProject,
    sharedData: SharedData,
    interactionType: InteractionState['type'],
    importState: ImportState
): [{[trackLocalId: string]: Track}, TrackDispatcher, number] {
    const {isAuthenticated} = useUser();

    const [tracks, setTracks] = useState<{[trackLocalId: string]: Track}>({});

    const [unsaved, addSavePromise] = useIsSaved();

    const dispatcher = useCallback(
        (action: TrackAction): string[] => {
            if (action.type === 'create') {
                return create(action, setTracks);
            } else if (action.type === 'remove') {
                return remove(action, setTracks);
            } else if (action.type === 'update') {
                return update(action, setTracks);
            } else if (action.type === 'takeLocal') {
                return takeLocal(action, tracks, setTracks, sharedData);
            } else if (action.type === 'updateProperties') {
                return updateProperties(action, setTracks);
            } else if (action.type === 'updatePointProperties') {
                return updatePointProperties(action, setTracks);
            } else if (action.type === 'bulkArchive') {
                return bulkArchive(action, setTracks);
            } else throw new Error('Unrecognized action type');
        },
        [sharedData, tracks]
    );

    // When a track has been updated locally, save those changes back to the server.
    useEffect(() => {
        if (isSavingAllowed(interactionType, importState, isAuthenticated)) {
            for (const localId in tracks) {
                const track = tracks[localId];

                // If a new folder has been created while importing,
                // file all imported tracks to it.
                if (
                    importState.status === 'saving' &&
                    importState.folder &&
                    track.properties.isLatestImport &&
                    !track.properties.parent_folder_id
                ) {
                    track.properties.parent_folder_id = importState.folder.id;
                }
                if (getIsTrackModifiedSinceSave(track)) {
                    addSavePromise(saveTrack(track, project, dispatcher));
                }
            }
        }
    }, [
        addSavePromise,
        dispatcher,
        importState,
        interactionType,
        isAuthenticated,
        project,
        tracks,
    ]);

    return [tracks, dispatcher, unsaved];
}
